import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { type RootState } from '@/store';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import {
  decryptPayload,
  encryptPayload,
  isEncryptedResponse,
  isPayloadEncryptionEnabled,
  shouldEncryptBody,
} from '@/lib/payloadCrypto';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const encryptedBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  if (!isPayloadEncryptionEnabled) return baseQuery(args, api, extraOptions);

  let finalArgs = args;
  if (typeof args !== 'string' && shouldEncryptBody(args.body)) {
    finalArgs = { ...args, body: await encryptPayload(args.body) };
  }

  const result = await baseQuery(finalArgs, api, extraOptions);
  if (isEncryptedResponse(result.data)) {
    try {
      return { data: await decryptPayload(result.data.payload), meta: result.meta };
    } catch (err) {
      console.error('Failed to decrypt response payload', err);
      return {
        error: { status: 'CUSTOM_ERROR' as const, error: 'Failed to decrypt response' },
        meta: result.meta,
      };
    }
  }
  return result;
};

const RATE_LIMIT_MESSAGE = 'Too many requests, wait a minute';

// A 401 from these means a wrong password or code. The auth guard's own 401 keeps Nest's
// default 'Unauthorized' message, and that one is still an expired session.
const CREDENTIAL_CHECK_URLS = [
  '/auth/login',
  '/auth/mfa/verify',
  '/mfa/setup',
  '/mfa/setup/confirm',
  '/mfa/result-manager/confirm',
];
const isCredentialFailure = (url: string, error: FetchBaseQueryError) =>
  CREDENTIAL_CHECK_URLS.includes(url) &&
  (error.data as { message?: string } | undefined)?.message !== 'Unauthorized';

// Every 429 gets the same shape, even a plain-text one from a proxy, which fetchBaseQuery
// reports as a PARSING_ERROR. The MFA lockout keeps its message so the login can spot it.
const withRateLimitMessage = <R extends { error?: FetchBaseQueryError }>(
  args: any,
  result: R
): R => {
  const { error } = result;
  const isRateLimited =
    error?.status === 429 || (error?.status === 'PARSING_ERROR' && error.originalStatus === 429);
  if (!isRateLimited) return result;

  const message = (error.data as { message?: unknown } | undefined)?.message;
  if (typeof message === 'string' && message.toLowerCase().includes('invalid codes')) return result;

  // Mutations show the error where they were submitted; queries have nowhere to show it.
  const isQuery = typeof args === 'string' || !args.method || args.method === 'GET';
  if (isQuery) toast.error(RATE_LIMIT_MESSAGE, { id: 'rate-limit' });
  return { ...result, error: { status: 429, data: { message: RATE_LIMIT_MESSAGE } } };
};

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  const result = withRateLimitMessage(args, await encryptedBaseQuery(args, api, extraOptions));
  const url = typeof args === 'string' ? args : args.url;
  if (result.error && result.error.status === 401 && !isCredentialFailure(url, result.error)) {
    console.warn('Unauthorized! Logging out user...');
    Cookies.remove('admin_authToken');
    setTimeout(() => {
      if (window.location.pathname === '/login') return;
      window.location.href = '/login';
    }, 2000);
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: [
    'User',
    'Auth',
    'FeatureFlags',
    'Common',
    'Bankers',
    'Account',
    'Crypto',
    'Requests',
    'GetNotification',
    'Members',
    'GetBanner',
    'Casino',
    'Getsupport',
    'Event',
    'Market',
    'BetConfig',
    'getBonus',
    'Affiliates',
    'AffiliateSlabs',
    'AffiliateSlabHistory',
    'SystemSettings',
    'Wallet',
    'Provider',
    'ConversionRate',
    'Network',
    'Bonus',
    'BonusApplicants',
    'BalanceSummary',
    'UserManagement',
    'GameAnalytics',
    'CasinoAnalytics',
    'BusinessAnalytics',
    'TopUsers',
    'Upi',
    'TopCategories',
    'DeviceBreakdown',
    'BonusAnalytics',
    'LoginSummary',
    'LiveGames',
    'BalanceSummaryDataApi',
    'PaymentGateway',
    'AgentSessionConfig',
    'ResultManagerMfa',
  ],
  endpoints: () => ({}),
});

// Export hooks for usage in functional components
export const {
  util: { getRunningQueriesThunk },
} = apiSlice;

// Export the api slice for use in store
export default apiSlice;
